import type {IssueFieldSelection} from '@github-ui/issue-fields-shared/issue-field'
import {parsePinnedIssueFields} from '@github-ui/issue-fields-shared/issue-field'
import {clientSideRelayFetchQueryRetained} from '@github-ui/relay-environment'
import {useEffect, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {graphql} from 'relay-runtime'

import type {
  useIssueFormSecondaryQuery,
  useIssueFormSecondaryQuery$data,
} from './__generated__/useIssueFormSecondaryQuery.graphql'

type useIssueFormSecondaryQueryProps = {
  repoId: string
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const useIssueFormSecondaryGraphqlQuery = graphql`
  query useIssueFormSecondaryQuery($id: ID!) {
    node(id: $id) {
      ... on Repository {
        securityPolicyUrl
        contributingFileUrl
        codeOfConductFileUrl
        supportFileUrl
        owner {
          ... on Organization {
            pinnedIssueFields(first: 10) {
              nodes {
                __typename
                ... on IssueFieldText {
                  id
                  name
                  dataType
                }
                ... on IssueFieldSingleSelect {
                  id
                  name
                  dataType
                }
                ... on IssueFieldDate {
                  id
                  name
                  dataType
                }
                ... on IssueFieldNumber {
                  id
                  name
                  dataType
                }
              }
            }
          }
        }
      }
    }
  }
`

export type SecondaryQueryData = {
  securityPolicyUrl: string | null
  contributingFileUrl: string | null
  codeOfConductFileUrl: string | null
  supportFileUrl: string | null
  orgPinnedFields: readonly IssueFieldSelection[] | null
}

export function useIssueFormSecondaryQuery({repoId}: useIssueFormSecondaryQueryProps): SecondaryQueryData | null {
  const environment = useRelayEnvironment()
  const [secondaryQueryData, setSecondaryQueryData] = useState<SecondaryQueryData | null>(null)

  useEffect(() => {
    clientSideRelayFetchQueryRetained<useIssueFormSecondaryQuery>({
      environment,
      query: useIssueFormSecondaryGraphqlQuery,
      variables: {
        id: repoId,
      },
    }).subscribe({
      next: (data: useIssueFormSecondaryQuery$data) => {
        const orgPinnedFields = parsePinnedIssueFields(
          data.node?.owner?.pinnedIssueFields?.nodes?.filter(node => node?.__typename !== '%other'),
        )

        setSecondaryQueryData({
          securityPolicyUrl: data.node?.securityPolicyUrl ?? null,
          contributingFileUrl: data.node?.contributingFileUrl ?? null,
          codeOfConductFileUrl: data.node?.codeOfConductFileUrl ?? null,
          supportFileUrl: data.node?.supportFileUrl ?? null,
          orgPinnedFields,
        })
      },
    })
  }, [environment, repoId])

  return secondaryQueryData
}
